import { z } from 'zod';
import { Production, EventType, EventDateType } from '../../../__types__';
import { searchApiPagingFromHttp$ } from 'v5/api/_common/query/pagination.http.schema';
import { rowLevelValidation } from 'v5/platform/row-level-validation';

export const production$ = z.object({
  id: z.number(),
  guid: z.string(),
  entity_type: z.number(),
  production_type_id: z.number(),
  title: z.string().nullable(),
  description: z.string().nullable(),
  icon: z.string().nullable(),
  timezone: z.string().nullable(),
  color: z.string().nullable(),
  code: z
    .union([
      z.coerce.string(),
      z.object({
        nodes: z.array(
          z.object({
            id: z.string()
          })
        )
      })
    ])
    .nullable(),
  production_date: z.object({
    id: z.number(),
    event_type: z.string().optional(),
    note: z.string().nullable().optional(),
    timezone: z.string().nullable().optional(),
    start_date: z.string(),
    start_timestamp: z.string().nullable().optional(),
    end_date: z.string(),
    end_timestamp: z.string().nullable().optional(),
    recurrence: z.string().nullable().optional(),
    estimated_daily_hours: z.number().nullable().optional(),
    status: z.string().nullable().optional(),
    legacy_user_id: z.string().nullable().optional(),
    created_mechanism: z.string().nullable().optional(),
    created_at: z.string(),
    updated_at: z.string(),
    entity_type: z.number().optional(),
    entity_id: z.number().optional(),
    all_day: z.boolean().nullable().optional(),
    estimated_start_time: z.string().nullable().optional(),
    estimated_end_time: z.string().nullable().optional(),
    include_weekends: z.boolean().nullable().optional(),
    estimated_daily_minutes: z.number().nullable().optional(),
    date_type: z.string().optional(),
    status_id: z.number().nullable().optional(),
    user_id: z.string().nullable().optional(),
    all_day_duration_hours: z.number().nullable().optional()
  }),
  status_id: z.number(),
  budget_currency: z.string(),
  budget_amount: z.number().nullable(),
  resourcing_at: z.string().nullable(),
  ready_at: z.string().nullable(),
  completed_at: z.string().nullable(),
  cancelled_at: z.string().nullable(),
  groups: z.array(z.any()),
  resource_slots: z.array(z.any()),
  budget_expenses: z.array(z.any()),
  events: z.array(z.any()),
  tags: z.array(z.any()),
  tasklist_id: z.number().nullable(),
  enterprise_workspace_id: z.number().nullish(),
  enterprise_team_id: z.number().nullish(),
  custom_fields: z.array(z.any()).optional(),
  custom_fieldgroups: z.array(z.any()).optional(),
  created_at: z.string(),
  updated_at: z.string(),
  archived: z.boolean(),
  user_id: z.string(),
  created_mechanism: z.string().nullable()
});

export type ProductionFromHttp = z.infer<typeof production$>;

/**
 * Maps wire production JSON to domain {@link Production}. Used after wire-level list parsing
 * (e.g. `rowLevelValidation.unsafeMapAll` in `production.api.http.ts`).
 */
export function productionFromHttp(http: ProductionFromHttp): Production {
  return {
    id: http.id,
    guid: http.guid,
    entity_type: http.entity_type,
    production_type_id: http.production_type_id,
    title: http.title || undefined,
    description: http.description || undefined,
    icon: http.icon || undefined,
    timezone: http.timezone || undefined,
    color: http.color || undefined,
    code: http.code
      ? typeof http.code === 'string' || typeof http.code === 'number'
        ? http.code
        : { nodes: http.code.nodes.map(n => ({ id: n.id, type: 'text' })) }
      : undefined,
    production_date: {
      id: http.production_date.id,
      event_type: (http.production_date.event_type ||
        'PRODUCTION') as EventType,
      entity_type: http.production_date.entity_type,
      entity_id: http.production_date.entity_id,
      note: http.production_date.note || undefined,
      timezone: http.production_date.timezone || undefined,
      date_type: (http.production_date.date_type ||
        'RECURRING_DEFAULT') as EventDateType,
      all_day: http.production_date.all_day || undefined,
      start_date: http.production_date.start_date,
      start_timestamp: http.production_date.start_timestamp
        ? new Date(http.production_date.start_timestamp)
        : undefined,
      end_date: http.production_date.end_date,
      end_timestamp: http.production_date.end_timestamp
        ? new Date(http.production_date.end_timestamp)
        : undefined,
      recurrence: http.production_date.recurrence || undefined,
      estimated_daily_hours:
        http.production_date.estimated_daily_hours || undefined,
      estimated_daily_minutes:
        http.production_date.estimated_daily_minutes || undefined,
      estimated_start_time:
        http.production_date.estimated_start_time || undefined,
      estimated_end_time: http.production_date.estimated_end_time || undefined,
      all_day_duration_hours: http.production_date.all_day_duration_hours || 0,
      include_weekends: http.production_date.include_weekends || undefined,
      status_id: http.production_date.status_id || undefined,
      status: http.production_date.status || undefined,
      created_at: new Date(http.production_date.created_at),
      updated_at: new Date(http.production_date.updated_at),
      user_id: http.production_date.user_id || null,
      created_mechanism: http.production_date.created_mechanism || null
    },
    status_id: http.status_id,
    budget_currency: http.budget_currency,
    budget_amount: http.budget_amount || 0,
    resourcing_at: http.resourcing_at
      ? new Date(http.resourcing_at)
      : undefined,
    ready_at: http.ready_at ? new Date(http.ready_at) : undefined,
    completed_at: http.completed_at ? new Date(http.completed_at) : undefined,
    cancelled_at: http.cancelled_at ? new Date(http.cancelled_at) : undefined,
    groups: http.groups,
    resource_slots: http.resource_slots,
    budget_expenses: http.budget_expenses,
    events: http.events,
    tags: http.tags,
    tasklist_id: http.tasklist_id || undefined,
    // null is a production of no workspace; missing, a copy that doesn't say
    enterprise_workspace_id: http.enterprise_workspace_id,
    enterprise_team_id: http.enterprise_team_id ?? undefined,
    custom_fields: http.custom_fields || [],
    custom_fieldgroups: http.custom_fieldgroups || [],
    created_at: new Date(http.created_at),
    updated_at: new Date(http.updated_at),
    archived: http.archived,
    user_id: http.user_id,
    created_mechanism: http.created_mechanism || null
  };
}

/**
 * Workspace `/search/productions`: wire row validation only. `{ items, count }` and domain
 * mapping are built in {@link createProductionApi} after {@link parseWithDecoratedError}.
 */
export const productionSearchListResponse$ = z.object({
  results: rowLevelValidation.withRowLevelValidation(production$),
  paging: searchApiPagingFromHttp$
});

/**
 * `/batch/productions/search` body: wire rows only. List shape and mapping in
 * {@link createProductionApi}.
 */
export const productionBatchSearchListResponse$ = z.object({
  data: rowLevelValidation.withRowLevelValidation(production$)
});
